import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { LockOutlined, MailOutlined, ShopOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  loginWithCredentials,
  selectIsAuthenticated,
  selectAuth,
} from '../../features/auth/authSlice';

const { Title, Paragraph } = Typography;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { isLoading, error } = useAppSelector(selectAuth);

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // The admin-portal is the platform operator console — accounts always live in
  // the dedicated 'platform' workspace, so we fix it here instead of asking
  // operators to type a slug they'd never know.
  const onFinish = (values: { email: string; password: string }) => {
    dispatch(loginWithCredentials({ ...values, workspace: 'platform' }));
  };

  const reason = (location.state as any)?.reason;

  return (
    <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
      <div className="text-center mb-6">
        <Title level={3} style={{ marginBottom: 4 }}>
          Admin Portal
        </Title>
        <Paragraph type="secondary">Sign in with your administrator account</Paragraph>
      </div>

      {reason === 'admin_required' && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="Admin access required"
          description="This account doesn't have administrator privileges."
        />
      )}
      {error && <Alert type="error" showIcon className="mb-4" message={error} />}

      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
        >
          <Input prefix={<MailOutlined />} placeholder="admin@company.com" autoComplete="username" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: 'Enter your password' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginBottom: 12 }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Form.Item style={{ marginBottom: 8 }}>
          <Button type="primary" htmlType="submit" block loading={isLoading}>
            Sign In
          </Button>
        </Form.Item>
      </Form>

      <Alert
        message="Admin access only"
        description="Restricted to System and Tenant Administrators. Employees use the main application."
        type="info"
        showIcon
        className="text-left mt-2"
      />
    </Card>
  );
};

export default LoginPage;
